import React, { useState, useEffect, useCallback } from 'react';
import { Coins, AlertTriangle, Users, Calculator } from 'lucide-react';
import api from '../../utils/api';
import Modal from '../../components/common/Modal';
import CountryFlag from '../../components/common/CountryFlag';
import { useToast } from '../../components/common/Toast';
import './Allocations.css';

function Allocations() {
  const toast = useToast();

  const [allocations, setAllocations] = useState([]);
  const [unallocated, setUnallocated] = useState([]);
  const [ptoYears, setPtoYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(true);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Bulk modal
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkAmount, setBulkAmount] = useState('');
  const [bulkSelected, setBulkSelected] = useState(new Set());

  // Proration calculator
  const [prorationOpen, setProrationOpen] = useState(false);
  const [prorationStartDate, setProrationStartDate] = useState('');
  const [prorationBase, setProrationBase] = useState('15');
  const [prorationResult, setProrationResult] = useState(null);

  // Bonus modal
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [bonusUserId, setBonusUserId] = useState('');
  const [bonusDays, setBonusDays] = useState('');
  const [bonusReason, setBonusReason] = useState('');

  // Load PTO years
  useEffect(() => {
    api.get('/admin/pto-years')
      .then(data => {
        const years = data.pto_years || [];
        setPtoYears(years);
        const current = years.find(y => y.is_current);
        if (current) setSelectedYear(current.id);
      })
      .catch(() => {
        // Don't loop -- PTO years may not be configured yet
        setPtoYears([]);
      });
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedYear) return;
    try {
      setLoading(true);
      const [allocRes, unallocRes] = await Promise.allSettled([
        api.get(`/allocations?pto_year_id=${selectedYear}`),
        api.get(`/allocations/missing?pto_year_id=${selectedYear}`),
      ]);
      setAllocations(allocRes.status === 'fulfilled' ? (allocRes.value.allocations || []) : []);
      setUnallocated(unallocRes.status === 'fulfilled' ? (unallocRes.value.users || []) : []);
    } catch (err) {
      // Don't loop
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Edit allocation
  const handleEditClick = (alloc) => {
    setEditUser(alloc);
    setEditAmount(alloc.total_days?.toString() || '0');
    setEditNotes(alloc.notes || '');
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      await api.post('/allocations', {
        user_id: editUser.user_id || editUser.id,
        pto_year_id: selectedYear,
        total_days: amount,
        notes: editNotes || undefined,
      });
      toast.success('Allocation updated');
      setEditModalOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to update allocation');
    } finally {
      setSaving(false);
    }
  };

  // Bulk set
  const toggleBulkSelect = (userId) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleBulkSave = async () => {
    const amount = parseFloat(bulkAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      await api.post('/allocations/bulk', {
        pto_year_id: selectedYear,
        allocations: [...bulkSelected].map(userId => ({
          user_id: userId,
          total_days: amount,
        })),
      });
      toast.success(`${bulkSelected.size} allocation(s) set`);
      setBulkModalOpen(false);
      setBulkSelected(new Set());
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to set allocations');
    } finally {
      setSaving(false);
    }
  };

  // Proration
  const handleProrate = async () => {
    if (!prorationStartDate || !prorationBase) return;
    try {
      const result = await api.get(
        `/allocations/prorate?start_date=${prorationStartDate}&total_days=${prorationBase}&pto_year_id=${selectedYear}`
      );
      setProrationResult(result);
    } catch (err) {
      toast.error('Failed to calculate proration');
    }
  };

  // Bonus
  const handleBonusAward = async () => {
    if (!bonusUserId || !bonusDays || !bonusReason.trim()) {
      toast.error('All fields are required');
      return;
    }

    setSaving(true);
    try {
      await api.post('/allocations/bonus', {
        user_id: bonusUserId,
        days: parseFloat(bonusDays),
        reason: bonusReason.trim(),
      });
      toast.success('Bonus days awarded');
      setBonusModalOpen(false);
      setBonusUserId('');
      setBonusDays('');
      setBonusReason('');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to award bonus');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="allocations-page">
      <div className="page-header">
        <Coins size={24} strokeWidth={2} />
        <h1>PTO Allocations</h1>
        <div className="page-header-actions">
          <button className="btn-secondary" onClick={() => setProrationOpen(true)}>
            <Calculator size={14} />
            Prorate
          </button>
          <button className="btn-secondary" onClick={() => setBonusModalOpen(true)}>
            <Coins size={14} />
            Award Bonus
          </button>
          {bulkSelected.size > 0 && (
            <button className="btn-primary" onClick={() => setBulkModalOpen(true)}>
              Bulk Set ({bulkSelected.size})
            </button>
          )}
        </div>
      </div>

      {/* Year selector */}
      <div className="allocations-controls">
        <select
          className="form-select"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
        >
          {ptoYears.map(y => (
            <option key={y.id} value={y.id}>{y.label}{y.is_current ? ' (Current)' : ''}</option>
          ))}
        </select>
      </div>

      {/* Unallocated warning */}
      {unallocated.length > 0 && (
        <div className="unallocated-warning">
          <AlertTriangle size={16} />
          <span>{unallocated.length} active employee{unallocated.length !== 1 ? 's' : ''} without allocation</span>
          <div className="unallocated-list">
            {unallocated.map(u => (
              <div key={u.id} className="unallocated-user">
                <span>{u.name}</span>
                <button
                  className="btn-sm"
                  onClick={() => {
                    setEditUser(u);
                    setEditAmount('0');
                    setEditNotes('');
                    setEditModalOpen(true);
                  }}
                >
                  Set
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Allocations table */}
      {loading ? (
        <div className="allocations-loading">Loading...</div>
      ) : (
        <div className="allocations-table">
          <div className="alloc-table-header">
            <span className="alloc-col-check">
              <input
                type="checkbox"
                onChange={() => {
                  if (bulkSelected.size === allocations.length) {
                    setBulkSelected(new Set());
                  } else {
                    setBulkSelected(new Set(allocations.map(a => a.user_id)));
                  }
                }}
                checked={allocations.length > 0 && bulkSelected.size === allocations.length}
              />
            </span>
            <span className="alloc-col-name">Employee</span>
            <span className="alloc-col-country">Country</span>
            <span className="alloc-col-days">Allocation</span>
            <span className="alloc-col-actions">Actions</span>
          </div>

          {allocations.length === 0 ? (
            <div className="alloc-empty">No allocations set for this PTO year</div>
          ) : (
            allocations.map(alloc => (
              <div key={alloc.id} className="alloc-row">
                <span className="alloc-col-check">
                  <input
                    type="checkbox"
                    checked={bulkSelected.has(alloc.user_id)}
                    onChange={() => toggleBulkSelect(alloc.user_id)}
                  />
                </span>
                <div className="alloc-col-name">
                  <span className="alloc-user-name">{alloc.name}</span>
                  <span className="alloc-user-email">{alloc.email}</span>
                </div>
                <span className="alloc-col-country">
                  {alloc.country_code ? (
                    <>
                      <CountryFlag code={alloc.country_code} size="small" />
                      {alloc.country_code}
                    </>
                  ) : '-'}
                </span>
                <span className="alloc-col-days alloc-days-value">{alloc.total_days}</span>
                <div className="alloc-col-actions">
                  <button className="btn-sm" onClick={() => handleEditClick(alloc)}>Edit</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Set Allocation" size="small">
        {editUser && (
          <>
            <p className="modal-subtitle">{editUser.name || editUser.first_name}</p>
            <div className="form-group" style={{ marginTop: '16px' }}>
              <label className="form-label">Total Days</label>
              <input
                type="number"
                className="form-input"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                step="0.5"
                min="0"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input
                type="text"
                className="form-input"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setEditModalOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleEditSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* Bulk Modal */}
      <Modal isOpen={bulkModalOpen} onClose={() => setBulkModalOpen(false)} title="Bulk Set Allocation" size="small">
        <p style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)', marginBottom: '16px' }}>
          Set allocation for {bulkSelected.size} selected employee{bulkSelected.size !== 1 ? 's' : ''}
        </p>
        <div className="form-group">
          <label className="form-label">Days per employee</label>
          <input
            type="number"
            className="form-input"
            value={bulkAmount}
            onChange={(e) => setBulkAmount(e.target.value)}
            step="0.5"
            min="0"
            autoFocus
          />
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => setBulkModalOpen(false)}>Cancel</button>
          <button className="btn-primary" onClick={handleBulkSave} disabled={saving}>
            {saving ? 'Setting...' : `Set for ${bulkSelected.size}`}
          </button>
        </div>
      </Modal>

      {/* Proration Calculator */}
      <Modal isOpen={prorationOpen} onClose={() => setProrationOpen(false)} title="Proration Calculator" size="small">
        <div className="form-group">
          <label className="form-label">Employee Start Date</label>
          <input
            type="date"
            className="form-input"
            value={prorationStartDate}
            onChange={(e) => setProrationStartDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Full Year Allocation</label>
          <input
            type="number"
            className="form-input"
            value={prorationBase}
            onChange={(e) => setProrationBase(e.target.value)}
            step="0.5"
            min="0"
          />
        </div>
        <button
          className="btn-primary"
          onClick={handleProrate}
          style={{ marginTop: '12px', width: '100%', justifyContent: 'center' }}
        >
          Calculate
        </button>
        {prorationResult && (
          <div className="proration-result">
            <div className="proration-value">{prorationResult.prorated_days} days</div>
            <div className="proration-calc">{prorationResult.calculation}</div>
            <div className="proration-months">{prorationResult.months_remaining} months remaining in PTO year</div>
          </div>
        )}
      </Modal>

      {/* Bonus Award Modal */}
      <Modal isOpen={bonusModalOpen} onClose={() => setBonusModalOpen(false)} title="Award Bonus Days" size="small">
        <div className="form-group">
          <label className="form-label">Employee</label>
          <select
            className="form-select"
            value={bonusUserId}
            onChange={(e) => setBonusUserId(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">Select employee</option>
            {allocations.map(a => (
              <option key={a.user_id} value={a.user_id}>{a.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Days</label>
          <input
            type="number"
            className="form-input"
            value={bonusDays}
            onChange={(e) => setBonusDays(e.target.value)}
            step="0.5"
            min="0.5"
          />
        </div>
        <div className="form-group">
          <label className="form-label form-label-required">Reason</label>
          <textarea
            className="form-textarea"
            value={bonusReason}
            onChange={(e) => setBonusReason(e.target.value)}
            placeholder="Why are these bonus days being awarded?"
            rows={3}
            style={{ width: '100%' }}
          />
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => setBonusModalOpen(false)}>Cancel</button>
          <button className="btn-primary" onClick={handleBonusAward} disabled={saving}>
            {saving ? 'Awarding...' : 'Award Bonus'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Allocations;
